// PeriodDays (timesheet rules §4): a person's pay period, a row per calendar day on one timeline. The
// hours on top (TimeScale, alone); each day's word (a link that opens the quick edit on it, in red only
// when the day has a problem), its clocked parts as still TimeBars on a TimeTrack, and paid on the
// right with "past 8 h" under a long day. A job's part names its customer and job ("Whitford ·
// #24611"); a part the office recorded for the tech (F-20) says who under the day ("Dana for Sam: …").
// A line after each week gives its paid minutes, and "past 40 h" when it applies; the overtime comes
// from the server's split, never the page. Reads only --pdays-*; TimeScale, TimeTrack, TimeBar and
// Link read their own.
import { Fragment } from 'react';
import { Link } from '../../components/Link/Link.tsx';
import { TimeBar } from '../../components/TimeBar/TimeBar.tsx';
import { TimeScale } from '../../components/TimeScale/TimeScale.tsx';
import { TimeTrack } from '../../components/TimeTrack/TimeTrack.tsx';
import { TimeSpanProvider } from '../../components/time-span.tsx';
import type { TimesheetView } from '../timesheet-view.ts';
import './PeriodDays.css';

export interface PeriodDaysProps {
  days: TimesheetView['days'];
  weeks: TimesheetView['weeks'];
  /** A day's word: the quick edit on that day. */
  onOpenDay?: (date: string) => void;
  density?: 'comfortable' | 'compact';
}

export function PeriodDays({ days, weeks, onOpenDay, density = 'compact' }: PeriodDaysProps) {
  const ends = days.flatMap((d) =>
    d.parts.flatMap((p) => [p.startMinute, p.endMinute ?? p.startMinute + 30]),
  );
  const from = Math.min(7 * 60, ...ends);
  const to = Math.max(18 * 60, ...ends.map((m) => m + 30));
  const weekAfter = new Map(weeks.map((w) => [w.to, w]));
  return (
    <TimeSpanProvider from={from} to={to} now={null} density={density} readOnly>
      <div className="fl-pdays">
        <span className="fl-pdays__corner" aria-hidden="true" />
        <div className="fl-pdays__scale">
          <TimeScale alone />
        </div>
        <span className="fl-pdays__paid-head">Paid</span>
        {days.map((d) => {
          const w = weekAfter.get(d.date);
          return (
            <Fragment key={d.date}>
              <span className="fl-pdays__day" data-bad={d.bad ? '' : undefined}>
                {onOpenDay ? (
                  <Link variant={d.bad ? 'danger' : 'inline'} onPress={() => onOpenDay(d.date)}>
                    {d.day}
                  </Link>
                ) : (
                  d.day
                )}
              </span>
              <div className="fl-pdays__track">
                <TimeTrack
                  label={d.day}
                  stack={false}
                  items={d.parts.map((p) => ({ ...p, start: p.startMinute, end: p.endMinute }))}
                  renderItem={(p, at) => (
                    <TimeBar
                      kind={p.kind}
                      from={at.from}
                      to={at.to}
                      label={p.label}
                      name={p.name}
                      pen={p.pen ?? null}
                      state={p.state}
                      toCheck={p.toCheck}
                      paid={p.paid}
                    />
                  )}
                />
              </div>
              <span className="fl-pdays__paid">
                {d.parts.length ? d.paid : <span className="fl-pdays__none">No taps</span>}
                {d.past8h ? <span className="fl-pdays__over">{d.past8h}</span> : null}
              </span>
              {d.recorded?.length ? (
                <p className="fl-pdays__recorded">
                  {d.recorded.map((r) => (
                    <span key={r}>{r}</span>
                  ))}
                </p>
              ) : null}
              {w ? (
                <p className="fl-pdays__week">
                  <span>{w.label}</span>
                  <span className="fl-pdays__week-paid">
                    {w.paid}
                    {w.past40h ? <span className="fl-pdays__over">{w.past40h}</span> : null}
                  </span>
                </p>
              ) : null}
            </Fragment>
          );
        })}
      </div>
    </TimeSpanProvider>
  );
}
